/**
 *  @class
 *  @function EditorialStoryTabs
 */
if (!customElements.get('editorial-story-tabs')) {
  class EditorialStoryTabs extends HTMLElement {
    constructor() {
      super();
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
    }
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('.editorial-story-tabs__toggle'));
      this.items = Array.from(this.querySelectorAll('.editorial-story-tabs__item'));
      this.panels = Array.from(this.querySelectorAll('.editorial-story-tabs__panel'));
      this.sliders = Array.from(this.querySelectorAll('slide-show'));
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reduceMotionQuery.matches;
      this.autoplayDuration = Number(this.dataset.autoplaySeconds || 5) * 1000;
      this.autoplayPaused = false;
      this.autoplayFrame = null;
      this.activeIndex = this.getInitialIndex();

      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => {
          if (!this.canHover()) {
            this.activate(index);
          }
        });
        button.addEventListener('mouseenter', () => {
          if (this.canHover()) {
            this.activate(index, { focus: false });
          }
        });
        button.addEventListener('focus', () => this.activate(index, { focus: false }));
        button.addEventListener('keydown', (event) => this.onKeyDown(event, index));
      });

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const item = event.target.closest('.editorial-story-tabs__item');
          const index = this.items.indexOf(item);
          if (index > -1) {
            this.activate(index, { focus: false });
          }
        });
      }

      this.activate(this.activeIndex, { focus: false, restartAutoplay: false });
      this.setupAutoplay();
    }
    disconnectedCallback() {
      this.stopAutoplay();
      this.removeEventListener('mouseenter', this.onPointerEnter);
      this.removeEventListener('mouseleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    canHover() {
      return this.hoverQuery.matches;
    }
    getInitialIndex() {
      const activeItem = this.items.findIndex((item) => item.classList.contains('is-active'));
      return activeItem > -1 ? activeItem : 0;
    }
    onKeyDown(event, index) {
      if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
        return;
      }
      event.preventDefault();

      let nextIndex = index;
      if (event.key === 'ArrowDown') {
        nextIndex = (index + 1) % this.buttons.length;
      } else if (event.key === 'ArrowUp') {
        nextIndex = (index - 1 + this.buttons.length) % this.buttons.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.buttons.length - 1;
      }

      this.activate(nextIndex);
    }
    setupAutoplay() {
      if (!this.autoplayEnabled || this.buttons.length < 2) {
        this.clearProgress();
        return;
      }

      this.addEventListener('mouseenter', this.onPointerEnter);
      this.addEventListener('mouseleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.resumeAutoplay();
    }
    onPointerEnter() {
      this.pauseAutoplay();
    }
    onPointerLeave() {
      this.resumeAutoplay();
    }
    onFocusIn() {
      this.pauseAutoplay();
    }
    onFocusOut() {
      window.requestAnimationFrame(() => {
        if (!this.contains(document.activeElement)) {
          this.resumeAutoplay();
        }
      });
    }
    onVisibilityChange() {
      if (document.hidden) {
        this.pauseAutoplay();
      } else {
        this.resumeAutoplay();
      }
    }
    pauseAutoplay() {
      if (!this.autoplayEnabled) {
        return;
      }
      this.autoplayPaused = true;
      this.stopAutoplay();
    }
    resumeAutoplay() {
      if (!this.autoplayEnabled) {
        return;
      }
      this.autoplayPaused = false;
      this.startAutoplay();
    }
    startAutoplay() {
      if (this.autoplayPaused || !this.autoplayEnabled || this.buttons.length < 2) {
        return;
      }
      this.stopAutoplay();
      this.clearProgress();
      this.autoplayStartedAt = performance.now();
      this.setProgress(this.activeIndex, 0);
      this.autoplayFrame = window.requestAnimationFrame((timestamp) => this.tickAutoplay(timestamp));
    }
    stopAutoplay() {
      if (this.autoplayFrame) {
        window.cancelAnimationFrame(this.autoplayFrame);
        this.autoplayFrame = null;
      }
    }
    tickAutoplay(timestamp) {
      if (this.autoplayPaused) {
        return;
      }

      const elapsed = timestamp - this.autoplayStartedAt;
      const progress = Math.min(elapsed / this.autoplayDuration, 1);
      this.setProgress(this.activeIndex, progress * 100);

      if (progress >= 1) {
        const nextIndex = (this.activeIndex + 1) % this.buttons.length;
        this.activate(nextIndex, { focus: false });
        return;
      }

      this.autoplayFrame = window.requestAnimationFrame((nextTimestamp) => this.tickAutoplay(nextTimestamp));
    }
    clearProgress() {
      this.items.forEach((item) => item.style.setProperty('--editorial-story-tabs-progress', '0%'));
    }
    setProgress(index, value) {
      const activeItem = this.items[index];
      if (!activeItem) {
        return;
      }
      activeItem.style.setProperty('--editorial-story-tabs-progress', `${value}%`);
    }
    activate(index, options = {}) {
      const { focus = true, restartAutoplay = true } = options;
      const activeButton = this.buttons[index];
      if (!activeButton) {
        return;
      }
      this.activeIndex = index;
      this.clearProgress();

      this.items.forEach((item, itemIndex) => {
        const isActive = itemIndex === index;
        item.classList.toggle('is-active', isActive);
      });

      this.buttons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === index;
        button.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
      });

      if (focus) {
        activeButton.focus();
      }

      this.sliders.forEach((slider) => {
        const flkty = Flickity.data(slider);
        if (flkty) {
          flkty.select(index);
        }
      });

      if (restartAutoplay && this.autoplayEnabled && !this.autoplayPaused) {
        this.startAutoplay();
      }
    }
  }
  customElements.define('editorial-story-tabs', EditorialStoryTabs);
}
